'use client';

import React from 'react';
import { X, Zap, BarChart2, Bell, Briefcase } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';
import { dismissTrialPopup } from '@/lib/api-client';

export default function TrialWelcomePopup() {
  const { user, showTrialPopup, trialPopupTitle, trialPopupMessage, clearTrialPopup } = useAuth();

  if (!showTrialPopup || !user) return null;

  const daysLeft = user.currentPeriodEnd
    ? Math.max(0, Math.ceil((new Date(user.currentPeriodEnd).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : 0;

  const handleDismiss = async () => {
    clearTrialPopup();
    try { await dismissTrialPopup(); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={handleDismiss}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-card-bg border border-default rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 text-fg-subtle hover:text-white transition-colors z-10"
        >
          <X size={18} />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400/20 to-sky-500/20 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Zap size={20} className="text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{trialPopupTitle || 'Welcome to Pro!'}</h2>
              <p className="text-xs text-fg-muted">Free trial &mdash; {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining</p>
            </div>
          </div>

          {/* Message */}
          <p className="text-sm text-slate-300 mb-4">
            {trialPopupMessage || 'You have full Pro access. Here\'s what you can explore:'}
          </p>
          <div className="space-y-2 mb-5">
            {[
              { icon: BarChart2, label: 'Advanced charts & intraday data' },
              { icon: Bell, label: 'Real-time price alerts' },
              { icon: Briefcase, label: 'Portfolio tracking & analytics' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm text-slate-300">
                <Icon size={14} className="text-emerald-400 flex-shrink-0" />
                {label}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-2.5 bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Got it, let&apos;s go!
            </button>
            <Link
              href="/pro"
              onClick={handleDismiss}
              className="px-4 py-2.5 bg-surface hover:bg-card-hover-bg text-slate-300 text-sm font-medium rounded-xl transition-colors border border-default"
            >
              View plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
